import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { TagIcon, SparklesIcon } from '@heroicons/react/24/outline';
import { useQuery, useMutation } from '@apollo/client';
import AttributeField from './AttributeField';
import { useGetCustomersByPersonId } from '~/services/customer';
import { GET_PROFILE_ATTRIBUTES } from '~/services/ProfileAttributes';
import { GET_PROFILE_VALUES, UPDATE_PROFILE_VALUE, CREATE_PROFILE_VALUE } from '~/services/ProfileValues';
import { UserIcon } from '~/components/screens/forms/components_forms_creator/Icons';

// Extended ProfileAttribute interface to match backend response
interface ProfileAttribute {
  _id?: string;
  id?: string;
  name: string;
  dataType: 'text' | 'number' | 'boolean' | 'date' | 'closelist' | 'openlist';
  description?: string;
  useAI: boolean;
  closelist?: {
    options: string[];
    multiple: boolean;
  };
}

interface CustomAttribute {
  id: string;
  name: string;
  value: string;
  type: string;
  updated_at?: string;
}

interface Customer {
  _id: string;
  person_id: string;
  name: string;
  email?: string;
  phone?: string;
  created_at: string;
  updated_at?: string;
  last_activity?: string;
  custom_attributes?: CustomAttribute[];
}

const CustomerAttributes = () => {
  const [t] = useTranslation('customer');
  const { customerId, agentId } = useParams();
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [loading, setLoading] = useState(true);
  const [attributeValues, setAttributeValues] = useState<{[key: string]: any}>({});
  const [editingAttributes, setEditingAttributes] = useState<{[key: string]: boolean}>({});
  const [editedValues, setEditedValues] = useState<{[key: string]: any}>({});
  const [profileValueIds, setProfileValueIds] = useState<{[key: string]: string}>({});
  const [saving, setSaving] = useState<{[key: string]: boolean}>({});
  
  const [getCustomer, { data, loading: queryLoading, error }] = useGetCustomersByPersonId();

  // Get profile attributes
  const { data: attributesData, loading: attributesLoading } = useQuery(GET_PROFILE_ATTRIBUTES, {
    variables: { data: { agent_id: agentId } },
    skip: !agentId,
  });

  // Get profile values
  const { data: valuesData, loading: valuesLoading, refetch: refetchValues } = useQuery(GET_PROFILE_VALUES, {
    variables: { data: { agent_id: agentId } },
    skip: !agentId,
  });

  // Mutations for updating/creating profile values
  const [updateProfileValue] = useMutation(UPDATE_PROFILE_VALUE);
  const [createProfileValue] = useMutation(CREATE_PROFILE_VALUE);

  // Process profile values when available
  useEffect(() => {
    if (valuesData?.get_profile_values?.data && !valuesLoading && customerId) {
      const profileValues = valuesData.get_profile_values.data;
      const valuesByAttribute: {[key: string]: any} = {};
      const profileValueIds: {[key: string]: string} = {};
      
      // Group values by attribute_id and person_id
      profileValues.forEach((value: any) => {
        if (value.person_id === customerId) {
          const attributeKey = value.profile_attribute_id;
          valuesByAttribute[attributeKey] = value.value;
          profileValueIds[attributeKey] = value._id; // Store the profile value ID for updates
        }
      });
      
      setAttributeValues(valuesByAttribute);
      setProfileValueIds(profileValueIds);
      setEditedValues(valuesByAttribute); // Initialize edited values with current values
    }
  }, [valuesData, valuesLoading, customerId]);

  // Load customer data on component mount
  useEffect(() => {
    const loadCustomer = async () => {
      if (!customerId || !agentId) return;
      
      setLoading(true);
      try {
        await getCustomer({
          variables: {
            data: {
              agent_id: agentId,
              person_id: customerId,
            },
          },
        });
      } catch (error) {
        console.error('Error loading customer:', error);
      } finally {
        setLoading(false);
      }
    };

    loadCustomer();
  }, [customerId, agentId, getCustomer]);

  // Handle GraphQL response
  useEffect(() => {
    if (data?.get_customers_by_person_id) {
      const response = data.get_customers_by_person_id;
      if (!response.hasError && response.data?.customer) {
        const customerData = response.data.customer;
        
        const formattedCustomer: Customer = {
          _id: customerData.person_id,
          person_id: customerData.person_id,
          name: customerData.profile_name || `${customerData.first_name || ''} ${customerData.last_name || ''}`.trim() || 'Unknown',
          email: customerData.personChannels?.find((ch: any) => ch.channel_type === 'email')?.channel_id || '',
          phone: customerData.personChannels?.find((ch: any) => ch.channel_type === 'whatsapp')?.channel_id || '',
          created_at: customerData.created_at,
          updated_at: customerData.updated_at,
          last_activity: customerData.updated_at,
          custom_attributes: (customerData.profileValues || []).map((pv: any) => ({
            id: pv._id,
            name: pv.profile_attribute_name,
            value: Array.isArray(pv.value) ? pv.value.join(', ') : pv.value,
            type: Array.isArray(pv.value) ? 'array' : 'string',
            updated_at: pv.updated_at,
          })),
        };
        
        setCustomer(formattedCustomer);
      } else {
        console.error('Error in response:', response.message);
        setCustomer(null);
      }
    }
  }, [data]);

  // Handle edit mode toggle
  const handleEditClick = (attributeId: string) => {
    setEditingAttributes(prev => ({
      ...prev,
      [attributeId]: true
    }));
    // Initialize edited value with current value
    setEditedValues(prev => ({
      ...prev,
      [attributeId]: attributeValues[attributeId] || ''
    }));
  };

  // Handle cancel edit
  const handleCancelEdit = (attributeId: string) => {
    setEditingAttributes(prev => ({
      ...prev,
      [attributeId]: false
    }));
    // Reset edited value to original
    setEditedValues(prev => ({
      ...prev,
      [attributeId]: attributeValues[attributeId] || ''
    }));
  };

  // Handle value change during edit
  const handleValueChange = (attributeId: string, value: any) => {
    setEditedValues(prev => ({
      ...prev,
      [attributeId]: value
    }));
  };

  // Handle save attribute
  const handleSaveAttribute = async (attributeId: string) => {
    if (!customerId || !agentId) return;

    setSaving(prev => ({ ...prev, [attributeId]: true }));

    try {
      const newValue = editedValues[attributeId];
      const existingProfileValueId = profileValueIds[attributeId];

      if (existingProfileValueId) {
        // Update existing profile value
        await updateProfileValue({
          variables: {
            data: {
              profile_value_id: existingProfileValueId,
              agent_id: agentId,
              value: newValue
            }
          }
        });
      } else {
        // Create new profile value
        await createProfileValue({
          variables: {
            data: {
              agent_id: agentId,
              profile_id: customerId, // person_id is used as profile_id
              attribute_id: attributeId,
              value: newValue
            }
          }
        });
      }

      // Update local state
      setAttributeValues(prev => ({
        ...prev,
        [attributeId]: newValue
      }));

      // Exit edit mode
      setEditingAttributes(prev => ({
        ...prev,
        [attributeId]: false
      }));

      // Refetch values to get updated data
      refetchValues();

    } catch (error) {
      console.error('Error saving attribute:', error);
      // Could add a toast notification here
    } finally {
      setSaving(prev => ({ ...prev, [attributeId]: false }));
    }
  };

  if (loading || queryLoading || attributesLoading || valuesLoading) {
    return (
      <div className="space-y-6">
        <div className="text-center py-12">
          <div className="loading loading-spinner loading-lg text-brand-primary"></div>
          <p className="text-brand-body/70 mt-4">{t('loading', 'Loading...')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="text-center py-12">
          <p className="text-brand-coral">{t('error_loading_customer', 'Error loading customer')}</p>
        </div>
      </div>
    );
  }

  // Get all available attributes from the profile attributes query
  const allAttributes: ProfileAttribute[] = attributesData?.get_profile_attributes?.data || [];
  
  // Create combined attributes with values when available
  const attributesWithValues = allAttributes.map(attr => {
    const attributeKey = attr.id || attr._id || '';
    const value = attributeValues[attributeKey];
    
    return {
      id: attributeKey,
      name: attr.name,
      value: value ? (Array.isArray(value) ? value.join(', ') : value.toString()) : '',
      type: attr.dataType,
      hasValue: !!value,
      updated_at: undefined // We don't have this from the current query structure
    };
  }).sort((a, b) => {
    // Sort by hasValue: true first, then false
    if (a.hasValue && !b.hasValue) return -1;
    if (!a.hasValue && b.hasValue) return 1;
    return 0;
  });

  return (
    <div className="space-y-6">
      {/* Attributes Header */}
      <div className="flex items-center gap-4">
        <TagIcon className="h-6 w-6 text-brand-primary" />
        <div>
          <h3 className="text-brand-heading text-xl font-semibold">{t('custom_attributes', 'Características del usuario')}</h3>
          <p className="text-brand-body/70">
            {t('attributes_description', 'Información adicional y campos personalizados del cliente')}
          </p>
        </div>
      </div>

      {/* Attributes Content */}
      {attributesWithValues.length === 0 ? (
        <div className="card-brand border border-gray-200/50 shadow-sm">
          <div className="card-body text-center py-12">
            <TagIcon className="h-12 w-12 text-brand-primary/30 mx-auto mb-4" />
            <h4 className="text-lg font-medium text-brand-primary mb-2">
              {t('no_attributes', 'Sin atributos personalizados')}
            </h4>
            <p className="text-brand-body/50">
              {t('no_attributes_description', 'Este cliente no tiene atributos personalizados configurados')}
            </p>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {attributesWithValues.map((attribute) => {
            const attributeId = attribute.id;
            const profileAttribute = allAttributes.find(attr => (attr.id || attr._id) === attributeId);

            if (!profileAttribute) return null;

            return (
              <div key={attribute.id} className="relative">
                <AttributeField
                  attribute={profileAttribute}
                  attributeId={attributeId}
                  value={editingAttributes[attributeId] ? editedValues[attributeId] : attributeValues[attributeId]}
                  isEditing={editingAttributes[attributeId]}
                  isSaving={saving[attributeId]}
                  onEdit={handleEditClick}
                  onCancel={handleCancelEdit}
                  onSave={handleSaveAttribute}
                  onChange={handleValueChange}
                  displayMode="card"
                  showAIIndicator={true}
                  showTypeIndicator={true}
                  updatedAt={attribute.updated_at}
                />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CustomerAttributes;
